/**
 * ModuleStatusStore — out-of-React channel for live module drive-status
 * updates (mirror of MoverPositionStore, but for warning/error overlays
 * on motor modules).
 *
 * The host pushes an array of `ModuleStatusEntry` values via
 * `XtsViewer3DRef.setModuleStatuses(array)`; the array is indexed by
 * `ModuleEntry.globalNumber`. Internally we store one entry per
 * globalNumber and let `<XtsModule>` subscribe via React context — the
 * store takes precedence over the config-time `ModuleEntry.status` so
 * PLC-streamed alarm data drives the visuals without forcing a re-render
 * of the whole config tree.
 *
 * Re-render trigger: status changes are infrequent compared to mover
 * positions (alarms, not motion). The store calls registered listeners
 * synchronously on each write so a single `useSyncExternalStore` per
 * module re-renders only when its own globalNumber changes.
 */

import React from 'react';

export interface ModuleStatusStoreEntry {
  warning?: boolean;
  error?: boolean;
}

type Listener = () => void;

export class ModuleStatusStore {
  private readonly entries = new Map<number, ModuleStatusStoreEntry>();
  private readonly listeners = new Map<number, Set<Listener>>();

  set(globalNumber: number, status: ModuleStatusStoreEntry | null): void {
    if (status === null) {
      this.entries.delete(globalNumber);
    } else {
      this.entries.set(globalNumber, status);
    }
    this.notify(globalNumber);
  }

  /**
   * Bulk update from a sparse array. `array[i]` corresponds to module
   * `globalNumber === i`; `undefined` / `null` entries are skipped (no
   * change). Use `clear(globalNumber)` to explicitly clear a slot.
   */
  setMany(array: ReadonlyArray<ModuleStatusStoreEntry | null | undefined>): void {
    const touched: number[] = [];
    for (let i = 0; i < array.length; i++) {
      const v = array[i];
      if (v === undefined) continue;
      if (v === null) {
        if (this.entries.delete(i)) touched.push(i);
      } else {
        const prev = this.entries.get(i);
        if (
          !prev ||
          prev.warning !== v.warning ||
          prev.error !== v.error
        ) {
          this.entries.set(i, v);
          touched.push(i);
        }
      }
    }
    for (const gn of touched) this.notify(gn);
  }

  get(globalNumber: number): ModuleStatusStoreEntry | undefined {
    return this.entries.get(globalNumber);
  }

  clear(globalNumber: number): void {
    if (this.entries.delete(globalNumber)) this.notify(globalNumber);
  }

  clearAll(): void {
    const all = Array.from(this.entries.keys());
    this.entries.clear();
    for (const gn of all) this.notify(gn);
  }

  subscribe(globalNumber: number, listener: Listener): () => void {
    let set = this.listeners.get(globalNumber);
    if (!set) {
      set = new Set();
      this.listeners.set(globalNumber, set);
    }
    set.add(listener);
    return () => {
      set!.delete(listener);
      if (set!.size === 0) this.listeners.delete(globalNumber);
    };
  }

  private notify(globalNumber: number): void {
    const set = this.listeners.get(globalNumber);
    if (!set) return;
    for (const l of set) l();
  }
}

/**
 * React context the scene tree uses to find the active store. Set up by
 * `<XtsViewer3D>` so every `<XtsModule>` reads the same instance.
 */
export const ModuleStatusStoreContext =
  React.createContext<ModuleStatusStore | null>(null);
